import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig.js";

import { CDM_FINDER_REPORT_TYPE } from "../constants/cdmFinder";
import {
  buildCdmFinderParams,
  hasCdmFinderCriteria,
} from "../utils/buildCdmFinderParams";
import { getQueryParamsFromObject } from "../utils/getQueryParamsFromObject";
import { getReportPath } from "../utils/getReportPath";

// Returns the filter query suffix already prefixed with "&". The finder needs
// repeatable array params, so it uses a dedicated URLSearchParams builder. The
// register requires a registerType, so default it when the tab first mounts.
const buildFilterQuery = (reportType, filters) => {
  if (reportType === CDM_FINDER_REPORT_TYPE) {
    return buildCdmFinderParams(filters);
  }
  if (reportType === "cdm-register") {
    return getQueryParamsFromObject({
      registerType: "TREATMENT",
      ...filters,
    }).replace("?", "&");
  }

  return getQueryParamsFromObject(filters).replace("?", "&");
};

const fetchReports = async (tenantVal, reportType, filters, page, rows) => {
  const axiosInstance = createAxiosInstance();

  const path = `${tenantVal}/reports/${getReportPath(reportType)}${
    rows === "All" ? "" : "/page"
  }`;

  const url = `${path}?page=${page}${
    rows === "All" ? "" : `&rows=${rows}`
  }${buildFilterQuery(reportType, filters)}`;

  const response = await axiosInstance.get(url);
  return response.data;
};

export const useReports = ({ tenantVal, reportType, filters, page, rows }) => {
  // Don't fire the finder with no criteria (it would return the whole practice).
  const isEnabled =
    reportType !== CDM_FINDER_REPORT_TYPE || hasCdmFinderCriteria(filters);

  const { data, isFetching, isError } = useQuery({
    queryKey: ["reports", reportType, filters, page, rows],
    queryFn: () => fetchReports(tenantVal, reportType, filters, page, rows),
    enabled: isEnabled,
    // Only keep previous data when paging within the same report type.
    // Tab switches and filter changes always clear the table immediately.
    placeholderData: (previousData, previousQuery) => {
      const prevKey = previousQuery?.queryKey;
      if (!prevKey) return undefined;
      const sameType = prevKey[1] === reportType;
      const sameFilters =
        JSON.stringify(prevKey[2]) === JSON.stringify(filters);
      return sameType && sameFilters ? previousData : undefined;
    },
  });

  return {
    items: data?.data ?? [],
    totalRecords: data?.totalRecords,
    isLoading: isFetching,
    isError,
  };
};
